import { Dialog, DialogTitle, Typography, IconButton, DialogContent, Box, TextField, Grid, DialogActions, Button } from '@mui/material'
import React, { useState, useEffect } from 'react'
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from 'react-hook-form';
import AuthService from '../services/auth.service';
import { toast } from 'react-toastify';


interface AccountVerificationProps {
    open: boolean;
    onProceed: () => void;
    onClose: () => void;
    phoneNumber: string;
    email: string;
    companyId: string;
    companyName: string;
    infLeadSquaredId: string;
}

const AccountVerification: React.FC<AccountVerificationProps> = ({ open, onClose, onProceed, phoneNumber, email, companyId, companyName, infLeadSquaredId }) => {
    const [timeLeft, setTimeLeft] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    
    const { control, watch, setValue } = useForm({
        defaultValues: {
            otp: ['', '', '', '', '', '']
        }
    });

    const otpValues = watch('otp');

    useEffect(() => {
        if (!open) {
            setTimeLeft(20);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 0) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open]);

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace') {
            if (!otpValues[index] && index > 0) {
                const prevInput = document.querySelector(
                    `input[name="otp.${index - 1}"]`
                ) as HTMLInputElement;
                if (prevInput) {
                    prevInput.focus();
                }
            }
        }
    };

    const handlePaste = (event: React.ClipboardEvent) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const pastedNumbers = pastedData.replace(/[^\d]/g, '').slice(0, 6);
        
        if (pastedNumbers) {
            const newOtp = [...otpValues];
            pastedNumbers.split('').forEach((digit, index) => {
                if (index < 6) newOtp[index] = digit;
            });
            newOtp.forEach((value, index) => {
                setValue(`otp.${index}`, value);
            });
        }
    };

    const handleResendCode = async () => {
        if (timeLeft === 0) {
            try {
                await AuthService.resendConfirmationCode(email);
                toast.success('Code resent successfully');
                setTimeLeft(20);
                
                const timer = setInterval(() => {
                    setTimeLeft((prevTime) => {
                        if (prevTime <= 0) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prevTime - 1;
                    });
                }, 1000);
            } catch (error: any) {
                toast.error('Failed to resend code');
                console.error('Failed to resend code:', error.message);
            }
        }
    };

    const formatPhoneNumber = (phone: string) => {
        const digits = phone?.replace(/\D/g, '');
        const lastTwoDigits = digits.slice(-2);
        return `*********${lastTwoDigits}`;
    };

    const handleClose = () => {
        [0, 1, 2, 3, 4, 5].forEach((index) => {
            setValue(`otp.${index}`, '');
        });
        onClose();
    };

    const handleProceed = async () => {
        try {
            setIsLoading(true);
            const confirmationCode = otpValues.join('');
            await AuthService.confirmSignup({
                email,
                confirmationCode,
                infLeadSquaredId,
                companyId,
                companyName
            });
            toast.success('Account verified successfully');
            onProceed();
        } catch (error: any) {
            toast.error(error.message);
            console.error('Confirmation failed:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth sx={{
            "& .MuiDialog-paper": {
                maxWidth: "506px",
                borderRadius: "16px",
                position: "relative",
            },
        }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                }}
            >
                <Typography
                    sx={{
                        fontSize: {
                            xs: "22px",
                        },
                        fontWeight: "500",
                        color: "#333333",
                    }}
                >
                    Account verification
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: "#666",
                        position: "absolute",
                        top: "12px",
                        right: "16px",
                        "&:hover": {
                            color: "#000",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ padding: "32px 20px", borderBottom: "1px solid #E8EAF0" }}>
                <Typography color='#010101' fontSize='18px' textAlign='center' mb='32px'>
                    Enter the 6-digit code we just sent to your email <Typography component="span" fontSize="inherit" fontStyle="italic">{email}</Typography>
                </Typography>
                <Box display="flex" gap="12px" justifyContent="center" mb='27px' flexWrap='wrap'>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <Controller
                            key={index}
                            name={`otp.${index}`}
                            control={control}
                            rules={{
                                pattern: /^[0-9]*$/
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!/^\d*$/.test(value)) return;
                                        
                                        field.onChange(value.slice(-1));
                                        
                                        if (value && index < 5) {
                                            const nextInput = document.querySelector(
                                                `input[name="otp.${index + 1}"]`
                                            ) as HTMLInputElement;
                                            if (nextInput) {
                                                nextInput.focus();
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                                    onPaste={handlePaste}
                                    sx={{
                                        width: '48px',
                                        "& .MuiOutlinedInput-root": {
                                            width: '48px',
                                            height: '48px',
                                            backgroundColor: "#F0F0F0",
                                            borderRadius: '8px',
                                            "& input": {
                                                padding: '0',
                                                textAlign: 'center',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: '#000000',
                                            }
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#F0F0F0",
                                        },
                                        "& .MuiOutlinedInput-input": {
                                            height: '100%',
                                        }
                                    }}
                                />
                            )}
                        />
                    ))}
                </Box>
                <Typography 
                    color='#1272CA' 
                    fontSize='15px' 
                    textAlign='center' 
                    fontWeight='500'
                    sx={{ 
                        cursor: timeLeft === 0 ? 'pointer' : 'default',
                        '&:hover': timeLeft === 0 ? {
                            textDecoration: 'underline'
                        } : {}
                    }}
                    onClick={handleResendCode}
                >
                    {timeLeft === 0 ? (
                        'Resend code'
                    ) : (
                        `Resend code in ${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')} sec`
                    )}
                </Typography>
            </DialogContent>
            {/* Dialog Actions */}
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "27px 20px 20px 20px",
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClose}
                    sx={{
                        textTransform: "capitalize",
                        px: "24px",
                        borderRadius: "8px",
                        border: "1px solid #E8EAF0",
                        color: "#2B2B30",
                        mr: "16px",
                        height: "47px!important",
                    }}
                >
                    Back
                </Button>

                <Button 
                    variant="contained" 
                    className="x-large-btn" 
                    disableElevation 
                    disabled={isLoading}
                    sx={{
                        textTransform: "capitalize", 
                        borderRadius: "8px", 
                        px: "28px", 
                        height: "47px!important",
                    }} 
                    onClick={handleProceed}
                >
                    {isLoading ? "Verifying..." : "Proceed"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
export default AccountVerification
